/* custome-texteditor.js */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '../dialog/dialog';
import PanelCard from '../panel-card/panel-card';
import './custom-texteditor.scss';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  LeftAlignIcon,
  CenterAlignIcon,
  RightAlignIcon,
  JustifiedAlignIcon,
  ImageOptionIcon,
  BulletListIcon,
  NumberListIcon,
  TableIconIcon,
} from './wrappedIcons';

import { RejectIcon, AcceptIcon, SendIcon, AttachIcon } from '../icons/icons';
import FileItem from '../file-item/file-item';
import { FormattIcon } from '../icons/editor';
import ListingStatus from '../listing-status/listing-status';
import MentionDropdown from './mention-list';
// import UserDetails from '../user-details/user-details';

/**
 * Unified helper: filter mention users by query.
 * Supports:
 * - First name
 * - Last name
 * - First + last name with space ("amar s", "amar singh", "si", etc.)
 */
const filterMentionUsers = (users, query) => {
  const q = (query || '').toLowerCase().trim();
  if (!q) return users.slice();

  const parts = q.split(/\s+/).filter(Boolean); // ["amar", "s"]

  return users.filter((u) => {
    const full = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
    return parts.every((p) => full.includes(p));
  });
};

function CustomEditor({
  getEditorValue,
  onSendClick,
  onAttachClick,
  chatFiles,
  onFileDiscardClick,
  enableBullateList,
  enableNumberList,
  enableTable,
  enableImageUpload,
  disabled,
  disabledSend,
  visibleOptions,
  charLimit,
  width,
  label,
  id,
  required,
  placeholder,
  disabledOptions,
  handleNoteData,
  className,
  disabledAttach,
  disableFontSize,
  disableUnderline,
  disableAlignment,
  movetoBottom,
  defaultvalue,
  mentionUsers: mentionUsersProp,
  onCharCountChange,
}) {
  const editorRef = useRef(null);
  const editorWrapperRef = useRef(null);

  // Editor UI state
  const [showDialog, setShowDialog] = useState(false);
  const [formatVisibility, setFormatVisibility] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [selectionRange, setSelectionRange] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [currentButton, setCurrentButton] = useState('');
  // const [visibleEditor, setVisibleEditor] = useState(true);

  // Mention related state
  const mentionUsers =
    mentionUsersProp && mentionUsersProp.length > 0 ? mentionUsersProp : [];

  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 });
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const mentionList = useRef(null);

  // formats mention's innerHTML to a simplified mention-tag
  const formatMessage = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return '';

    // Find all mentions
    const mentionNodes = editor.querySelectorAll('.mention-tag');

    // Replace .mention-tag spans → <mention> tags
    let htmlContent = editor.innerHTML;
    mentionNodes.forEach((node) => {
      const userId = node.getAttribute('data-user-id') || '';
      const displayText = node.innerText || '';
      const mentionTag = `<mention data-user-id="${userId}">${displayText}</mention>`;
      htmlContent = htmlContent.replace(node.outerHTML, mentionTag);
    });

    // Normalize line breaks and remove editor artifacts
    const cleanedText = htmlContent
      .replace(/&nbsp;/g, ' ')
      .replace(/<div>/g, '<br>')
      .replace(/<\/div>/g, '')
      .trim();

    return cleanedText;
  }, []);

  // -----------------------
  // Formatting helpers
  // -----------------------
  const applyStyle = (style, value) => {
    document.execCommand(style, false, value);
    if (editorRef.current) editorRef.current.focus();
    const content = editorRef.current ? editorRef.current.innerHTML : '';
    getEditorValue(content);

    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
  };

  const updateFormattingState = useCallback(() => {
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
  }, []);

  useEffect(() => {
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.innerHTML = defaultvalue || '';
    }
  }, [defaultvalue]);

  // -----------------------
  // Paste / Cut handling
  // -----------------------
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return undefined;

    // const handlePasteCut = (event) => {
    //   event.preventDefault();
    //   const text = event.clipboardData.getData('text');

    //   document.execCommand('insertText', false, text);

    //   const updatedCharCount = editor.innerText.replace(/\s/g, '').length;
    //   setCharCount(updatedCharCount);
    //   getEditorValue(updatedCharCount > 0 ? editor.innerHTML : '');
    //   handleNoteData(updatedCharCount > 0 ? editor.innerHTML : '');
    // };

    const handlePasteCut = (event) => {
      event.preventDefault();
      const editorContainer = editorRef.current;
      if (!editorContainer) return;

      const text = event.clipboardData.getData('text/plain') || '';

      // Build safe inline HTML
      const html = text.replace(
        /@([A-Za-z][A-Za-z\s]*)/g,
        (match, namePart) => {
          const full = namePart.trim().toLowerCase();

          const user = mentionUsers.find((u) => {
            const fullName = `${u.firstName} ${u.lastName}`
              .trim()
              .toLowerCase();
            return fullName === full;
          });

          if (!user) return match; // keep plain text if no match

          return `
        <span 
          class="mention-tag editable-mention"
          contenteditable="true"
          data-user-id="${user.userSub || user.id}"
        >
          @${user.firstName} ${user.lastName}
        </span>&nbsp;
      `;
        }
      );

      // Insert inline (NO <br>, NO div)
      document.execCommand('insertHTML', false, html);

      // Update state
      const updatedCharCount = editor.innerText.replace(/\s/g, '').length;
      setCharCount(updatedCharCount);
      getEditorValue(updatedCharCount > 0 ? editor.innerHTML : '');
      handleNoteData(updatedCharCount > 0 ? editor.innerHTML : '');
    };

    editor.addEventListener('paste', handlePasteCut);
    editor.addEventListener('cut', handlePasteCut);

    return () => {
      editor.removeEventListener('paste', handlePasteCut);
      editor.removeEventListener('cut', handlePasteCut);
    };
  }, [charLimit, getEditorValue, handleNoteData]);

  // -----------------------
  // Char count helper
  // -----------------------
  const updateCharCountAndValue = useCallback(() => {
    const updatedCharCount =
      editorRef?.current?.innerText?.replace(/\s/g, '').length || 0;
    setCharCount(updatedCharCount);
    getEditorValue(updatedCharCount > 0 ? formatMessage() : '');
    handleNoteData(updatedCharCount > 0 ? editorRef.current.innerHTML : '');
  }, [getEditorValue, handleNoteData, formatMessage]);

  useEffect(() => {
    if (typeof onCharCountChange === 'function') {
      onCharCountChange(charCount);
    }
  }, [charCount, onCharCountChange]);
  // -----------------------
  // NEW simplified: live mention-tag helpers
  // -----------------------

  // create an active, editable mention tag (user types inside this)
  const createActiveMentionTag = useCallback(() => {
    const span = document.createElement('span');
    span.className = 'mention-tag active-mention';
    span.setAttribute('data-query', '');
    span.setAttribute('contenteditable', 'true');
    span.textContent = '@';
    return span;
  }, []);

  const getCaretCoordinatesFromNode = useCallback((node) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      const rects = range.getClientRects();
      if (rects && rects.length > 0) return rects[rects.length - 1];
      // fallback: temporary zero-width span
      const tmp = document.createElement('span');
      tmp.appendChild(document.createTextNode('\u200b'));
      node.appendChild(tmp);
      const r = tmp.getBoundingClientRect();
      tmp.parentNode.removeChild(tmp);
      return r;
    } catch (e) {
      return null;
    }
  }, []);

  // Insert final mention replacing the active tag
  const insertMentionNode = useCallback(
    (user) => {
      const editor = editorRef.current;
      if (!editor) return;

      const activeTag = editor.querySelector('.active-mention');
      if (!activeTag) return;

      // Create final mention (editable so user can edit and it can convert to plain text)
      const finalTag = document.createElement('span');
      finalTag.className = 'mention-tag editable-mention';
      finalTag.setAttribute('contenteditable', 'false');
      finalTag.setAttribute('data-user-id', user.userSub || user.id || '');
      // store canonical full name for exact-match checks
      finalTag.setAttribute(
        'data-full-name',
        `${(user.firstName || '').trim()} ${(user.lastName || '').trim()}`
          .trim()
          .toLowerCase()
      );

      const displayName = `@${(user.firstName || '').trim()} ${(
        user.lastName || ''
      ).trim()}`.trim();
      finalTag.textContent = displayName;

      // Replace active mention with final
      activeTag.replaceWith(finalTag);

      // Ensure exactly one space after the mention
      let next = finalTag.nextSibling;
      if (!next) {
        next = document.createTextNode(' ');
        finalTag.after(next);
      } else if (next.nodeType === Node.TEXT_NODE) {
        next.textContent = ` ${next.textContent.replace(/^\s+/, '')}`;
      } else {
        const space = document.createTextNode(' ');
        finalTag.parentNode.insertBefore(space, next);
        next = space;
      }

      // Force caret outside the mention (after the space)
      setTimeout(() => {
        try {
          const sel = window.getSelection();
          const range = document.createRange();
          range.setStartAfter(next);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          editor.focus();
        } catch (e) {
          console.debug('insertMentionNode: caret placement failed', e);
        }
      }, 0);

      // Reset mention state
      setIsMentioning(false);
      setMentionQuery('');
      setFilteredUsers([]);
      setActiveMentionIndex(0);

      updateCharCountAndValue();

      // Add input listener to auto-convert to plain text when edited & no longer matches
      // (use once so we don't attach multiple handlers)
      const onInput = () => {
        const text = finalTag.textContent ? finalTag.textContent.trim() : '';
        const cleaned = text.replace(/^@/, '').trim().toLowerCase();

        // if the display is empty, convert to nothing
        if (cleaned.length === 0) {
          const plain = document.createTextNode('');
          finalTag.replaceWith(plain);
          const sel = window.getSelection();
          const r = document.createRange();
          r.setStartAfter(plain);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
          return;
        }

        // Find exact match against mentionUsers by full name
        const matched = mentionUsers.find((u) => {
          const full = `${(u.firstName || '').trim()} ${(
            u.lastName || ''
          ).trim()}`
            .trim()
            .toLowerCase();
          return full === cleaned;
        });

        if (!matched) {
          // convert to plain text (keep the edited text)
          const plain = document.createTextNode(text);
          finalTag.replaceWith(plain);
          const sel = window.getSelection();
          const r = document.createRange();
          r.setStartAfter(plain);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        }
      };

      // attach once
      finalTag.addEventListener('input', onInput, { once: false });
    },
    [mentionUsers, updateCharCountAndValue]
  );

  // Remove active mention tag (cancel mention)
  const removeActiveMentionTag = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const activeTag = editor.querySelector('.active-mention');
    if (!activeTag) return;

    // convert active mention to plain text (preserve typed content)
    const text = activeTag.textContent || '';
    const plain = document.createTextNode(text);

    // Replace and put caret after it
    activeTag.replaceWith(plain);

    const sel = window.getSelection();
    const range = document.createRange();
    range.setStartAfter(plain);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    setIsMentioning(false);
    setMentionQuery('');
    setFilteredUsers([]);
    setActiveMentionIndex(0);
  }, []);

  // -----------------------
  // selectionchange tracking (do not close while typing inside active tag)
  // -----------------------
  useEffect(() => {
    const onSelectionChange = () => {
      const active = document.activeElement;

      const insideEditor =
        editorRef.current && editorRef.current.contains(active);

      const activeTag =
        editorRef.current && editorRef.current.querySelector('.active-mention');

      const inDropdown =
        mentionList.current && mentionList.current.contains(active);

      if (!insideEditor && !inDropdown && !activeTag) {
        setIsMentioning(false);
        setFilteredUsers([]);
      }
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, []);

  // -----------------------
  // Close mention on outside click
  // -----------------------
  useEffect(() => {
    const handleClickOutside = ({ target }) => {
      if (
        editorRef.current &&
        !editorRef.current.contains(target) &&
        !mentionList.current?.contains(target)
      ) {
        removeActiveMentionTag();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [removeActiveMentionTag]);

  useEffect(() => {
    if (!mentionList.current) return;

    const container = mentionList.current;
    const items = container.querySelectorAll('.mention-item');

    const activeItem = items[activeMentionIndex];
    if (!activeItem) return;

    // Ensure DOM is painted before scrolling
    requestAnimationFrame(() => {
      activeItem.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'auto',
      });
    });
  }, [activeMentionIndex, filteredUsers]);

  // Update filtered users when mentionUsers or query changes (fallback)
  useEffect(() => {
    if (isMentioning) {
      const list = filterMentionUsers(mentionUsers, mentionQuery);
      setFilteredUsers(list);
      setActiveMentionIndex(0);
    }
  }, [mentionUsers, mentionQuery, isMentioning]);

  // -----------------------
  // Editor keyboard handlers (modified for active-tag approach)
  // -----------------------
  const handleEditorKeyDown = (event) => {
    updateFormattingState();

    const editor = editorRef.current;
    const activeTag =
      editor && editor.querySelector && editor.querySelector('.active-mention');

    // ─────────────────────────────────────────────
    // PREVENT <div> INSERTION ON ENTER
    // Replace with <br> only
    // ─────────────────────────────────────────────
    if (!isMentioning && event.key === 'Enter') {
      event.preventDefault();

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const range = sel.getRangeAt(0);

      // Insert <br>
      const br = document.createElement('br');
      range.deleteContents();
      range.insertNode(br);

      // Move caret after <br>
      const newRange = document.createRange();
      newRange.setStartAfter(br);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);

      updateCharCountAndValue();
      return; // Stop default div insertion
    }

    // ───────────────────────────────
    // Mention dropdown navigation
    // ───────────────────────────────
    if (isMentioning && filteredUsers.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveMentionIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveMentionIndex((i) => Math.max(i - 1, 0));
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const user = filteredUsers[activeMentionIndex];
        if (user) insertMentionNode(user);
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        removeActiveMentionTag();
        return;
      }
    }

    // ───────────────────────────────
    // Close mention when @ removed via Backspace
    // ───────────────────────────────
    if (
      event.key === 'Backspace' &&
      activeTag &&
      activeTag.textContent.length <= 1
    ) {
      event.preventDefault();
      removeActiveMentionTag();
      return;
    }

    // ───────────────────────────────
    // Create mention on '@'
    // ───────────────────────────────
    if (event.key === '@') {
      const sel = window.getSelection();
      const node = sel?.focusNode;

      let before = '';
      if (node && node.nodeType === Node.TEXT_NODE) {
        before = node.textContent.slice(0, sel.focusOffset);
      }

      if (/\w$/.test(before)) return;

      event.preventDefault();

      const range = sel.getRangeAt(0);
      range.deleteContents();

      const tag = createActiveMentionTag();
      range.insertNode(tag);

      const newRange = document.createRange();
      newRange.selectNodeContents(tag);
      newRange.collapse(false);
      sel.removeAllRanges();
      sel.addRange(newRange);

      setIsMentioning(true);
      setMentionQuery('');
      setFilteredUsers(mentionUsers.slice());
      setActiveMentionIndex(0);

      const rect = tag.getBoundingClientRect();
      setMentionPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });

      return;
    }

    if ((event.ctrlKey || event.metaKey) && event.key === 'a') return;

    if (event.key === 'Enter' && event.target.tagName === 'A') {
      window.open(event.target.href, '_blank');
    }

    if (editorRef.current && editorRef.current.innerHTML === '<br>') {
      editorRef.current.innerHTML = '';
    }
  };

  const isModifierKey = (key) =>
    key === 'Shift' || key === 'Control' || key === 'Alt' || key === 'Meta';

  const updateMentionPosition = (activeTag) => {
    if (!activeTag || !editorWrapperRef.current) return;

    const caretRect = getCaretCoordinatesFromNode(activeTag);
    const wrapperRect = editorWrapperRef.current.getBoundingClientRect();

    const top = caretRect.bottom - wrapperRect.top;
    const left = caretRect.left - wrapperRect.left;

    setMentionPos({
      top: top + 6,
      left,
    });
  };

  // handle typing inside editor: update active tag state if present, otherwise normal refresh
  const handleEditorKeyUp = ({ key }) => {
    if (isModifierKey(key)) return;

    const editor = editorRef.current;
    if (!editor) return;

    // small delay for character updates already typed
    setTimeout(updateCharCountAndValue, 0);

    const activeTag = editor.querySelector('.active-mention');

    // -------------------------
    // Typing inside active mention
    // -------------------------
    if (activeTag) {
      const text = activeTag.textContent || '';
      const rawQuery = text.slice(1).trim(); // remove leading @
      const lookupQuery = rawQuery.toLowerCase();

      console.debug('activeTag input:', { rawQuery, lookupQuery });

      // Find partials
      const partialList = filterMentionUsers(mentionUsers, lookupQuery);

      // Find exact full-name match
      const exactMatchUser = mentionUsers.find((u) => {
        const full = `${(u.firstName || '').trim()} ${(
          u.lastName || ''
        ).trim()}`
          .trim()
          .toLowerCase();
        return full === lookupQuery;
      });

      // Rule (Option 1):
      // - Keep dropdown open if there are partial matches OR an exact match
      // - If no partials and no exact match => convert to plain text and close dropdown
      if (partialList.length === 0 && !exactMatchUser) {
        // convert to plain text preserving '@...' text
        const plain = document.createTextNode(text);
        activeTag.replaceWith(plain);

        const sel = window.getSelection();
        const range = document.createRange();
        range.setStartAfter(plain);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);

        // reset mentioning state
        setIsMentioning(false);
        setFilteredUsers([]);
        setActiveMentionIndex(0);
        setMentionQuery('');

        editor.focus();
        return;
      }

      // If we get here -> partials exist or exact match exists -> show / update list
      setFilteredUsers(partialList);
      activeTag.dataset.query = rawQuery;
      setMentionQuery(rawQuery);

      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(key)) {
        setActiveMentionIndex(0);
      }

      // Update dropdown position
      updateMentionPosition(activeTag);
      return;
    }

    // -------------------------
    // Detect plain-text "@..." typed and convert to active mention
    // (only if not preceded by an alnum character)
    // -------------------------
    const sel = window.getSelection();
    if (sel && sel.focusNode && sel.focusNode.nodeType === Node.TEXT_NODE) {
      const nodeText = sel.focusNode.textContent;
      const caretPos = sel.focusOffset;
      const before = nodeText.slice(0, caretPos);

      // match @... at the end, not preceded by alnum
      const match = before.match(/(^|[^a-zA-Z0-9])@([\w\s]+)$/);
      if (match) {
        const rawQuery = match[2].trim();
        const lookupQuery = rawQuery.toLowerCase();

        const partialList = filterMentionUsers(mentionUsers, lookupQuery);
        const exactMatchUser = mentionUsers.find((u) => {
          const full = `${(u.firstName || '').trim()} ${(
            u.lastName || ''
          ).trim()}`
            .trim()
            .toLowerCase();
          return full === lookupQuery;
        });

        // convert only if partials exist OR exact match exists
        if (partialList.length > 0 || exactMatchUser) {
          const range = sel.getRangeAt(0);

          const span = document.createElement('span');
          span.className = 'mention-tag active-mention';
          span.setAttribute('contenteditable', 'true');
          span.textContent = `@${rawQuery}`;

          // replace the @... substring in the current text node
          const start = before.lastIndexOf('@');
          range.setStart(sel.focusNode, start);
          range.setEnd(sel.focusNode, caretPos);
          range.deleteContents();
          range.insertNode(span);

          // place caret at the end of the active tag
          const caretRange = document.createRange();
          caretRange.selectNodeContents(span);
          caretRange.collapse(false);
          sel.removeAllRanges();
          sel.addRange(caretRange);

          setIsMentioning(true);
          setFilteredUsers(partialList);
          setActiveMentionIndex(0);
          setMentionQuery(rawQuery);
          return;
        }

        // else: no partials and no exact match -> do nothing (no dropdown)
      }
    }

    // No active mention -> hide list
    setIsMentioning(false);
    setFilteredUsers([]);
  };

  useEffect(() => {
    if (defaultvalue === '' || defaultvalue == null) {
      setCharCount(0);
    } else {
      const newCount = defaultvalue.replace(/\s/g, '').length;
      setCharCount(newCount);
    }
  }, [defaultvalue]);

  // -----------------------
  // Other editor helpers (font size, alignment, images, lists)
  // -----------------------
  const handleChangeFontSize = (e) => {
    const fontSize = e.target.value;
    document.execCommand('fontSize', false, '7');
    const fontElements = document.getElementsByTagName('font');
    for (let i = 0; i < fontElements.length; i += 1) {
      if (fontElements[i].size === '7') {
        fontElements[i].removeAttribute('size');
        fontElements[i].style.fontSize = `${fontSize}px`;
      }
    }
    if (editorRef.current) editorRef.current.focus();
  };

  const handleAlignmentChange = (alignment) => {
    document.execCommand(`justify${alignment}`, false, null);
    if (editorRef.current) editorRef.current.focus();
    setCurrentButton(`justify${alignment}`);
  };

  const handleInsertImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target.result;
        if (editorRef.current) editorRef.current.appendChild(img);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClick = (e) => {
    if (e.target.tagName === 'A') {
      window.open(e.target.href, '_blank');
      // return;
    }

    // If user clicked inside a mention tag (or its child), open profile
    // const mentionEl = e.target.closest && e.target.closest('.mention-tag');
    // if (mentionEl) {
    //   const userId = mentionEl.getAttribute('data-user-id');
    //   // if (userId) {
    //   //   window.open(`/user/${userId}`, '_blank');
    //   // }
    // }
  };

  const handleInsertBulletList = () => applyStyle('insertUnorderedList', null);
  const handleInsertNumberList = () => applyStyle('insertOrderedList', null);

  const handleDialogAccept = () => {
    if (!urlInput.trim()) {
      setShowDialog(false);
      return;
    }

    const selection = window.getSelection();
    if (selectionRange) {
      const link = document.createElement('a');
      link.href = urlInput;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.appendChild(selectionRange.extractContents());
      selectionRange.insertNode(link);

      const newRange = document.createRange();
      newRange.setStartAfter(link);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      setSelectionRange(null);
    } else {
      const link = document.createElement('a');
      link.href = urlInput;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = urlInput;
      if (editorRef.current) editorRef.current.appendChild(link);
    }

    setShowDialog(false);
    setUrlInput('');
  };

  const handleSubmitMessage = () => {
    const editor = editorRef.current;
    if (!editor) return;

    onSendClick(formatMessage());

    // Reset editor
    editor.innerHTML = '';
    setCharCount(0);
    setIsBold(false);
    setIsItalic(false);
    setIsUnderline(false);
  };

  const handleMentionClick = (index) => {
    const user = filteredUsers[index];
    if (user) insertMentionNode(user);
  };

  // -----------------------
  // Render
  // -----------------------
  return (
    <>
      <div className="custom-editor-container" style={{ position: 'relative' }}>
        {label && (
          <label htmlFor={id}>
            {label} {required && <span className="required">*</span>}
          </label>
        )}

        <div
          className={`custom-editor${
            chatFiles.length !== 0 || formatVisibility || visibleOptions
              ? ' larged'
              : ''
          }${disabled ? ' disabled-box' : ''}`}
          style={{ width }}
          ref={editorWrapperRef}
        >
          {(formatVisibility || visibleOptions) && (
            <div className="toolbar-wrapper top">
              <div className="toolbar">
                {!movetoBottom && (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isBold ? ' active' : ''}`}
                        onClick={() => applyStyle('bold', null)}
                      >
                        <BoldIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isItalic ? ' active' : ''}`}
                        onClick={() => applyStyle('italic', null)}
                      >
                        <ItalicIcon />
                      </button>
                    </div>
                    {!disableUnderline && (
                      <div className="option-wrapper">
                        <button
                          type="button"
                          className={`toolbar-btn${
                            isUnderline ? ' active' : ''
                          }`}
                          onClick={() => applyStyle('underline', null)}
                        >
                          <UnderlineIcon />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!disableFontSize && (
                  <div className="fontsize-options">
                    <div className="option-wrapper">
                      <select onChange={handleChangeFontSize}>
                        {[10, 12, 14, 16, 18, 20, 24, 28, 32].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {!disableAlignment && (
                  <div className="textalign-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyLeft' ? 'active' : ''
                        }`}
                        onClick={() => handleAlignmentChange('Left')}
                      >
                        <LeftAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyCenter' ? 'active' : ''
                        }`}
                        onClick={() => handleAlignmentChange('Center')}
                      >
                        <CenterAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyRight' ? 'active' : ''
                        }`}
                        onClick={() => handleAlignmentChange('Right')}
                      >
                        <RightAlignIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          currentButton === 'justifyFull' ? 'active' : ''
                        }`}
                        onClick={() => handleAlignmentChange('Full')}
                      >
                        <JustifiedAlignIcon />
                      </button>
                    </div>
                  </div>
                )}

                <div className="bullet-list">
                  {enableBullateList && (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn"
                        onClick={handleInsertBulletList}
                      >
                        <BulletListIcon />
                      </button>
                    </div>
                  )}
                  {enableNumberList && (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn"
                        onClick={handleInsertNumberList}
                      >
                        <NumberListIcon />
                      </button>
                    </div>
                  )}
                  {enableTable && (
                    <div className="option-wrapper">
                      <button type="button" className="toolbar-btn">
                        <TableIconIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div
            ref={editorRef}
            className={`editor-content contentEditable${
              chatFiles.length !== 0 ? ' file-added' : ''
            } ${
              className === '' && disabledOptions ? 'h-150' : ''
            } ${className}`}
            contentEditable
            role="textbox"
            tabIndex="0"
            onClick={handleClick}
            onKeyDown={handleEditorKeyDown}
            onKeyUp={handleEditorKeyUp}
            onInput={updateCharCountAndValue}
            placeholder={placeholder}
            suppressContentEditableWarning
          />

          {chatFiles.length !== 0 && (
            <div className="file-list">
              {chatFiles.map((doc) => (
                <FileItem
                  key={doc.name}
                  file={doc}
                  onDiscard={onFileDiscardClick}
                />
              ))}
            </div>
          )}

          {!disabledSend && charLimit && (
            <div
              className={`char-count editor ${
                charCount >= charLimit ? 'exceed-limit' : ''
              }`}
            >
              {charCount}/{charLimit}
            </div>
          )}

          {!disabledOptions && (
            <div className="toolbar-wrapper justify-between">
              <div className="toolbar">
                {!movetoBottom ? (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn ${
                          formatVisibility ? 'active' : ''
                        }`}
                        onClick={() => setFormatVisibility(!formatVisibility)}
                      >
                        <FormattIcon
                          activeColor={formatVisibility ? '#ff1111' : 'none'}
                        />
                      </button>
                    </div>
                    {!disabledAttach && (
                      <div className="option-wrapper">
                        <label className="toolbar-btn">
                          <AttachIcon />
                          <input
                            type="file"
                            className="attachment"
                            onChange={onAttachClick}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    )}
                    {enableImageUpload && (
                      <div className="option-wrapper">
                        <label className="toolbar-btn">
                          <ImageOptionIcon />
                          <input
                            type="file"
                            className="attachment"
                            onChange={handleInsertImage}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="inline-options">
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isBold ? ' active' : ''}`}
                        onClick={() => applyStyle('bold', null)}
                      >
                        <BoldIcon />
                      </button>
                    </div>
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className={`toolbar-btn${isItalic ? ' active' : ''}`}
                        onClick={() => applyStyle('italic', null)}
                      >
                        <ItalicIcon />
                      </button>
                    </div>
                    {!disableUnderline && (
                      <div className="option-wrapper">
                        <button
                          type="button"
                          className={`toolbar-btn${
                            isUnderline ? ' active' : ''
                          }`}
                          onClick={() => applyStyle('underline', null)}
                        >
                          <UnderlineIcon />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="toolbar">
                <div className="inline-options">
                  {!disabledSend ? (
                    <div className="option-wrapper">
                      <button
                        type="button"
                        className="toolbar-btn send"
                        onClick={handleSubmitMessage}
                        disabled={charCount > charLimit}
                      >
                        <SendIcon color="#ffffff" />
                      </button>
                    </div>
                  ) : (
                    <div className="option-wrapper">
                      {charLimit && (
                        <div
                          className={`char-count editor ${
                            charCount >= charLimit ? 'exceed-limit' : ''
                          }`}
                        >
                          {charCount}/{charLimit}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <Dialog
            isShowing={showDialog}
            hide={() => setShowDialog(false)}
            placement="center"
          >
            <PanelCard>
              <div>
                <h5>Enter the URL</h5>
                <div className="d-flex align-center">
                  <input
                    className="mr-5"
                    type="text"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                  />
                  <div className="dialog-buttons">
                    <button
                      type="button"
                      style={{ border: 'none', background: 'none' }}
                      onClick={handleDialogAccept}
                    >
                      <AcceptIcon color="#55b938" />
                    </button>
                    <button
                      type="button"
                      style={{ border: 'none', background: 'none' }}
                      onClick={() => setShowDialog(false)}
                    >
                      <RejectIcon color="#f02b1f" />
                    </button>
                  </div>
                </div>
              </div>
            </PanelCard>
          </Dialog>
        </div>
      </div>
      {isMentioning && (
        <MentionDropdown
          mentionPos={mentionPos}
          mentionListRef={mentionList}
          filteredUsers={filteredUsers}
          activeMentionIndex={activeMentionIndex}
          handleMentionClick={handleMentionClick}
          setActiveMentionIndex={setActiveMentionIndex}
          ListingStatusComponent={ListingStatus}
          bottomSpace={editorWrapperRef.current.getBoundingClientRect().height}
        />
      )}
    </>
  );
}

CustomEditor.propTypes = {
  getEditorValue: PropTypes.func,
  onSendClick: PropTypes.func,
  onAttachClick: PropTypes.func,
  chatFiles: PropTypes.instanceOf(Array),
  onFileDiscardClick: PropTypes.func,
  enableBullateList: PropTypes.bool,
  enableNumberList: PropTypes.bool,
  enableTable: PropTypes.bool,
  enableImageUpload: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledSend: PropTypes.bool,
  visibleOptions: PropTypes.bool,
  charLimit: PropTypes.number,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  disabledOptions: PropTypes.bool,
  handleNoteData: PropTypes.func,
  className: PropTypes.string,
  disabledAttach: PropTypes.bool,
  disableFontSize: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  disableAlignment: PropTypes.bool,
  movetoBottom: PropTypes.bool,
  defaultvalue: PropTypes.string,
  mentionUsers: PropTypes.arrayOf(
    PropTypes.shape({
      userSub: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      profilePicture: PropTypes.string,
      userType: PropTypes.string,
    })
  ),
  onCharCountChange: PropTypes.func,
};

CustomEditor.defaultProps = {
  getEditorValue: () => {},
  onSendClick: () => {},
  onAttachClick: () => {},
  chatFiles: [],
  onFileDiscardClick: () => {},
  enableBullateList: false,
  enableNumberList: false,
  enableTable: false,
  enableImageUpload: false,
  disabled: false,
  disabledSend: false,
  visibleOptions: false,
  charLimit: null,
  width: '',
  placeholder: '',
  label: '',
  id: '',
  required: false,
  disabledOptions: false,
  handleNoteData: () => {},
  className: '',
  disabledAttach: false,
  disableFontSize: false,
  disableUnderline: false,
  disableAlignment: false,
  movetoBottom: false,
  defaultvalue: '',
  mentionUsers: [],
  onCharCountChange: () => {},
};

export default CustomEditor;
